import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  LifeBuoy, Loader2, Send, Plus, Clock, CheckCircle, AlertCircle,
  XCircle, MessageSquare, User, Shield, ChevronRight, Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const statusConfig = {
  open: { label: 'Abierto', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  in_progress: { label: 'En proceso', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  resolved: { label: 'Resuelto', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  closed: { label: 'Cerrado', color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

const categoryLabels = {
  general: 'General',
  subscription: 'Suscripción',
  billing: 'Facturación',
  technical: 'Técnico',
  feature: 'Nueva función',
};

export default function SupportPage() {
  const { isSuperAdmin } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', category: 'general', priority: 'normal' });
  const [creating, setCreating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const pg = usePagination(filterStatus);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const [ticketsRes, statsRes] = await Promise.all([
        api.get(`/api/support/tickets${params}`),
        api.get('/api/support/stats'),
      ]);
      setTickets(ticketsRes.data?.data || []);
      setStats(statsRes.data);
    } catch {
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, [filterStatus]);

  const loadTicketDetail = async (ticketId) => {
    setSelected(ticketId);
    setLoadingDetail(true);
    try {
      const res = await api.get(`/api/support/tickets/${ticketId}`);
      setSelectedTicket(res.data?.data || null);
      setReplyText('');
    } catch {
      toast.error('Error al cargar ticket');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      const res = await api.post(`/api/support/tickets/${selected}/respond`, { message: replyText });
      setSelectedTicket(res.data?.data || null);
      setReplyText('');
      toast.success('Respuesta enviada');
      fetchTickets();
    } catch {
      toast.error('Error al responder');
    } finally {
      setSending(false);
    }
  };

  const handleCreate = async () => {
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Completa todos los campos');
      return;
    }
    setCreating(true);
    try {
      await api.post('/api/support/tickets', newTicket);
      toast.success('Ticket creado exitosamente');
      setCreateOpen(false);
      setNewTicket({ subject: '', message: '', category: 'general', priority: 'normal' });
      fetchTickets();
    } catch {
      toast.error('Error al crear ticket');
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (ticketId, newStatus) => {
    try {
      const res = await api.put(`/api/support/tickets/${ticketId}/status`, { status: newStatus });
      setSelectedTicket(res.data?.data || null);
      toast.success('Estado actualizado');
      fetchTickets();
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const t = selectedTicket;

  return (
    <div>
      <TopBar
        title="Soporte"
        onMenuClick={() => setMobileMenuOpen(true)}
        churchInfo={churchInfo}
        actionButton={
          <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={() => setCreateOpen(true)} data-testid="support-create-button">
            <Plus className="w-4 h-4 mr-1" /> Nuevo ticket
          </Button>
        }
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            {[{k:'open',l:'Abiertos',c:'text-blue-700'},{k:'in_progress',l:'En proceso',c:'text-yellow-700'},{k:'resolved',l:'Resueltos',c:'text-green-700'},{k:'closed',l:'Cerrados',c:'text-gray-500'},{k:'total',l:'Total',c:'text-foreground'}].map(s => (
              <Card key={s.k} className="stat-card">
                <CardContent className="p-3 text-center">
                  <p className={`text-2xl font-semibold ${s.c}`}>{stats[s.k] || 0}</p>
                  <p className="text-[11px] text-muted-foreground">{s.l}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-3 mb-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="open">Abiertos</SelectItem>
              <SelectItem value="in_progress">En proceso</SelectItem>
              <SelectItem value="resolved">Resueltos</SelectItem>
              <SelectItem value="closed">Cerrados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : tickets.length === 0 ? (
          <div className="empty-state">
            <Inbox className="w-12 h-12 text-muted-foreground/40" />
            <p>No hay tickets de soporte</p>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white mt-2" onClick={() => setCreateOpen(true)}>Crear primer ticket</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[500px]">
            {/* Ticket List */}
            <div className="lg:col-span-5">
              <ScrollArea className="h-[600px] lg:h-[calc(100vh-280px)]">
                <div className="space-y-2 pr-2">
                  <Pagination
                    total={tickets.length} page={pg.page} pageSize={pg.pageSize}
                    onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
                    label="tickets" testId="support-pagination"
                  />
                  {tickets.slice(pg.offset, pg.offset + pg.pageSize).map(ticket => {
                    const st = statusConfig[ticket.status] || statusConfig.open;
                    const isSelected = selected === ticket.id;
                    const responseCount = ticket.responses?.length || 0;
                    return (
                      <Card
                        key={ticket.id}
                        className={`cursor-pointer transition-colors ${isSelected ? 'ring-2 ring-[#00CCFF] bg-accent/50' : 'hover:bg-accent/30'}`}
                        onClick={() => loadTicketDetail(ticket.id)}
                        data-testid={`ticket-${ticket.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${st.color}`}>
                              {st.label}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {new Date(ticket.created_at).toLocaleDateString('es-ES')}
                            </span>
                          </div>
                          <h4 className="font-medium text-sm mt-1">{ticket.subject}</h4>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{ticket.message}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-[10px] text-muted-foreground">
                              {isSuperAdmin ? `${ticket.user_name || ticket.user_email} • ${ticket.church_name || 'Sin iglesia'}` : categoryLabels[ticket.category] || ticket.category}
                            </span>
                            {responseCount > 0 && (
                              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                <MessageSquare className="w-3 h-3" /> {responseCount}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>

            {/* Ticket Detail */}
            <div className="lg:col-span-7">
              {loadingDetail ? (
                <Card className="h-full flex items-center justify-center min-h-[400px]">
                  <Loader2 className="w-8 h-8 spinner text-[#056A84]" />
                </Card>
              ) : t ? (
                <Card className="h-full flex flex-col">
                  <CardContent className="p-5 flex-1 flex flex-col">
                    {/* Header */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${(statusConfig[t.status] || statusConfig.open).color}`}>
                          {(statusConfig[t.status] || statusConfig.open).label}
                        </span>
                        <Badge variant="secondary" className="text-[10px]">{categoryLabels[t.category] || t.category}</Badge>
                        <Badge variant="outline" className="text-[10px]">{t.priority}</Badge>
                        {t.assigned_agent && (
                          <Badge variant="secondary" className="text-[10px]" data-testid="ticket-assigned-agent">
                            Agente: {t.assigned_agent}
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold">{t.subject}</h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <User className="w-3 h-3" />
                        <span>{t.user_name || t.user_email}</span>
                        {t.church_name && <><span>•</span><span>{t.church_name}</span></>}
                        <span>•</span>
                        <span>{new Date(t.created_at).toLocaleString('es-ES')}</span>
                      </div>
                    </div>

                    {/* Original message */}
                    <div className="p-3 bg-muted/40 rounded-lg mb-4">
                      <p className="text-sm whitespace-pre-wrap">{t.message}</p>
                    </div>

                    {/* Responses */}
                    {t.responses && t.responses.length > 0 && (
                      <ScrollArea className="flex-1 max-h-[300px] mb-4">
                        <div className="space-y-3">
                          {t.responses.map((r) => {
                            const isSupport = r.author_role === 'admin' || r.author_role === 'support';
                            return (
                            <div key={`${r.timestamp}-${r.author_id}`} className={`p-3 rounded-lg ${isSupport ? 'bg-[#056A84]/5 border border-[#056A84]/10 ml-4' : 'bg-muted/30 mr-4'}`} data-testid={`ticket-response-${r.timestamp}`}>
                              <div className="flex items-center gap-2 mb-1">
                                {isSupport ? (
                                  <Shield className="w-3 h-3 text-[#056A84]" />
                                ) : (
                                  <User className="w-3 h-3 text-muted-foreground" />
                                )}
                                <span className="text-[11px] font-medium">
                                  {r.author_name}{isSupport ? ' (Soporte)' : ''}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {new Date(r.timestamp).toLocaleString('es-ES')}
                                </span>
                              </div>
                              <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                            </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    )}

                    {/* SuperAdmin status controls */}
                    {isSuperAdmin && t.status !== 'closed' && (
                      <div className="flex gap-2 mb-3 flex-wrap">
                        {t.status !== 'in_progress' && (
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => updateStatus(t.id, 'in_progress')}>
                            <Clock className="w-3 h-3 mr-1" /> En proceso
                          </Button>
                        )}
                        {t.status !== 'resolved' && (
                          <Button size="sm" variant="outline" className="text-xs text-green-700" onClick={() => updateStatus(t.id, 'resolved')}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Resuelto
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="text-xs text-gray-500" onClick={() => updateStatus(t.id, 'closed')}>
                          <XCircle className="w-3 h-3 mr-1" /> Cerrar
                        </Button>
                      </div>
                    )}

                    {/* Reply */}
                    {t.status !== 'closed' && (
                      <div className="mt-auto border-t pt-3">
                        <Textarea
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          placeholder="Escribe una respuesta..."
                          rows={3}
                          className="mb-2"
                          data-testid="support-reply-input"
                        />
                        <Button
                          className="bg-[#056A84] hover:bg-[#045A70] text-white"
                          onClick={handleReply}
                          disabled={sending || !replyText.trim()}
                          data-testid="support-reply-send"
                        >
                          {sending ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                          Enviar respuesta
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full flex items-center justify-center min-h-[400px]">
                  <div className="text-center">
                    <LifeBuoy className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Selecciona un ticket para ver los detalles</p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuevo Ticket de Soporte</DialogTitle>
            <DialogDescription>Describe tu consulta o problema y te responderemos a la brevedad.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Asunto *</Label>
              <Input value={newTicket.subject} onChange={e => setNewTicket({...newTicket, subject: e.target.value})} placeholder="Ej: Consulta sobre upgrade de plan" data-testid="ticket-subject-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={newTicket.category} onValueChange={v => setNewTicket({...newTicket, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="subscription">Suscripción</SelectItem>
                    <SelectItem value="billing">Facturación</SelectItem>
                    <SelectItem value="technical">Técnico</SelectItem>
                    <SelectItem value="feature">Nueva función</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select value={newTicket.priority} onValueChange={v => setNewTicket({...newTicket, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baja</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mensaje *</Label>
              <Textarea value={newTicket.message} onChange={e => setNewTicket({...newTicket, message: e.target.value})} rows={5} placeholder="Describe tu consulta con el mayor detalle posible..." data-testid="ticket-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleCreate} disabled={creating} data-testid="ticket-submit-button">
              {creating ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Enviar ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
